import { useEffect, useRef, useState } from "react";
import { BarChart2, HardDrive, Info, MonitorCog, PenTool, Smartphone, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ColorPicker } from "@/components/ui/ColorPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePwaInstall } from "@/hooks/use-pwa-install";
import { usePwaUpdate } from "@/hooks/usePwaUpdate";
import { useSettingsStore } from "@/stores/settingsStore";
import { useDrawingStore } from "@/stores/drawingStore";
import { DRAWING_TOOLS } from "@/lib/lytick/drawingToolDefinitions";
import type { DrawingToolId } from "@/stores/drawingStore";
import { TimezoneSelect } from "@/components/features/TimezoneSelect";
import { APP_VERSION } from "@/lib/version";

interface SettingsPanelProps {
  open: boolean;
  onClose: () => void;
}

type TabId = "display" | "chart" | "drawings" | "app";

interface Snapshot {
  showOHLC: boolean;
  hapticsEnabled: boolean;
  soundAlertsEnabled: boolean;
  chartAppearance: ReturnType<typeof useSettingsStore.getState>["chartAppearance"];
}

function captureSnapshot(): Snapshot {
  const s = useSettingsStore.getState();
  return {
    showOHLC: s.showOHLC,
    hapticsEnabled: s.hapticsEnabled,
    soundAlertsEnabled: s.soundAlertsEnabled,
    chartAppearance: { ...s.chartAppearance },
  };
}

function restoreSnapshot(snap: Snapshot) {
  useSettingsStore.setState({
    showOHLC: snap.showOHLC,
    hapticsEnabled: snap.hapticsEnabled,
    soundAlertsEnabled: snap.soundAlertsEnabled,
    chartAppearance: snap.chartAppearance,
  });
}

function ToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div
      className="flex min-h-11 items-center justify-between gap-3 rounded-lg border px-3 py-2"
      style={{
        backgroundColor: "var(--bg-elevated)",
        borderColor: "var(--border-default)",
      }}
    >
      <div className="min-w-0">
        <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)" }}>{label}</p>
        <p
          className="mt-1 leading-4"
          style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}
        >
          {description}
        </p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="interactive workspace-switch"
      />
    </div>
  );
}

function SegmentedRow<T extends string>({
  label,
  description,
  options,
  value,
  onChange,
}: {
  label: string;
  description: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      className="flex min-h-11 items-center justify-between gap-3 rounded-lg border px-3 py-2"
      style={{
        backgroundColor: "var(--bg-elevated)",
        borderColor: "var(--border-default)",
      }}
    >
      <div className="min-w-0">
        <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)" }}>{label}</p>
        <p
          className="mt-1 leading-4"
          style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}
        >
          {description}
        </p>
      </div>
      <div
        className="flex shrink-0 rounded-md border overflow-hidden"
        style={{ borderColor: "var(--border-default)" }}
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                backgroundColor: active ? "var(--accent-primary)" : "transparent",
                color: active ? "var(--text-primary)" : "var(--text-secondary)",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: "display", label: "Display", icon: <MonitorCog className="h-4 w-4" /> },
  { id: "chart", label: "Chart", icon: <BarChart2 className="h-4 w-4" /> },
  { id: "drawings", label: "Drawings", icon: <PenTool className="h-4 w-4" /> },
  { id: "app", label: "App", icon: <Smartphone className="h-4 w-4" /> },
];

export function SettingsPanel({ open, onClose }: SettingsPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>("display");
  const snapshotRef = useRef<Snapshot | null>(null);

  const {
    showOHLC,
    hapticsEnabled,
    soundAlertsEnabled,
    chartAppearance,
    autoLoadCsv,
    setShowOHLC,
    setHapticsEnabled,
    setSoundAlertsEnabled,
    setChartAppearance,
    setTimezone,
    setAutoLoadCsv,
  } = useSettingsStore();
  const { canInstall, installLabel, isInstalled, promptInstall } = usePwaInstall({
    hasMeaningfulEngagement: true,
  });
  const { hasUpdateAvailable, reloadApp } = usePwaUpdate();

  useEffect(() => {
    if (open) {
      snapshotRef.current = captureSnapshot();
    }
  }, [open]);

  const handleCancel = () => {
    if (snapshotRef.current) restoreSnapshot(snapshotRef.current);
    snapshotRef.current = null;
    onClose();
  };

  const handleOk = () => {
    snapshotRef.current = null;
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
      <DialogContent
        showCloseButton={false}
        className="max-w-[640px] p-0 gap-0 flex flex-col overflow-hidden"
        style={{
          backgroundColor: "var(--bg-surface)",
          borderColor: "var(--border-default)",
          height: 520,
        }}
      >
        <DialogTitle className="sr-only">Settings</DialogTitle>

        {/* Header */}
        <div
          className="flex h-10 shrink-0 items-center justify-between border-b px-4"
          style={{
            backgroundColor: "var(--bg-elevated)",
            borderColor: "var(--border-default)",
          }}
        >
          <h2 style={{ color: "var(--text-primary)", fontSize: "var(--text-lg)", fontWeight: 600 }}>
            Settings
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            className="interactive interactive-icon inline-flex h-8 w-8 items-center justify-center rounded-md"
            style={{ color: "var(--text-secondary)" }}
            aria-label="Close settings"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body — tab rail + content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left tab rail */}
          <div
            className="flex shrink-0 flex-col border-r py-2"
            style={{
              width: 140,
              backgroundColor: "var(--bg-elevated)",
              borderColor: "var(--border-default)",
            }}
          >
            {TABS.map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: active ? 500 : 400,
                    color: active ? "var(--text-primary)" : "var(--text-secondary)",
                    backgroundColor: active ? "var(--surface-tint-hover)" : "transparent",
                    borderLeft: active
                      ? "2px solid var(--accent-primary)"
                      : "2px solid transparent",
                  }}
                >
                  <span style={{ color: active ? "var(--accent-primary)" : "var(--text-muted)" }}>
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Right content pane */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {activeTab === "display" && (
              <div className="space-y-2">
                <ToggleRow
                  label="Show OHLC status"
                  description="Keep open, high, low, and close data visible on the chart."
                  checked={showOHLC}
                  onCheckedChange={setShowOHLC}
                />
                <ToggleRow
                  label="Haptic feedback"
                  description="Vibrate on supported input actions."
                  checked={hapticsEnabled}
                  onCheckedChange={setHapticsEnabled}
                />
                <ToggleRow
                  label="Sound alerts"
                  description="Play confirmation sounds for key workspace actions."
                  checked={soundAlertsEnabled}
                  onCheckedChange={setSoundAlertsEnabled}
                />
              </div>
            )}

            {activeTab === "chart" && (
              <div className="space-y-2">
                <ToggleRow
                  label="Show grid"
                  description="Display horizontal and vertical grid lines on the chart."
                  checked={chartAppearance.gridVisible}
                  onCheckedChange={(checked) => setChartAppearance({ gridVisible: checked })}
                />
                <SegmentedRow
                  label="Crosshair"
                  description="Show a crosshair that follows cursor movement."
                  options={[
                    { value: "cross" as const, label: "Cross" },
                    { value: "none" as const, label: "None" },
                  ]}
                  value={chartAppearance.crosshairStyle}
                  onChange={(v) => setChartAppearance({ crosshairStyle: v })}
                />
                <SegmentedRow
                  label="Scale"
                  description="Set the y-axis scaling mode for price display."
                  options={[
                    { value: "normal" as const, label: "Normal" },
                    { value: "log" as const, label: "Log" },
                  ]}
                  value={chartAppearance.scaleMode}
                  onChange={(v) => setChartAppearance({ scaleMode: v })}
                />
                <SegmentedRow
                  label="Candle colors"
                  description="Western: green up / red down. East Asian: red up / green down."
                  options={[
                    { value: "western" as const, label: "Western" },
                    { value: "east-asian" as const, label: "East Asian" },
                  ]}
                  value={chartAppearance.candleConvention ?? "western"}
                  onChange={(v) => setChartAppearance({ candleConvention: v })}
                />
                <ToggleRow
                  label="Chart watermark"
                  description="Show the LYTICK brand mark at low opacity on the chart."
                  checked={chartAppearance.showWatermark ?? true}
                  onCheckedChange={(checked) => setChartAppearance({ showWatermark: checked })}
                />
                <div
                  className="flex min-h-11 items-center justify-between gap-3 rounded-lg border px-3 py-2"
                  style={{
                    backgroundColor: "var(--bg-elevated)",
                    borderColor: "var(--border-default)",
                  }}
                >
                  <div className="min-w-0">
                    <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)" }}>Timezone</p>
                    <p
                      className="mt-1 leading-4"
                      style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}
                    >
                      Chart X-axis time zone for candle timestamps.
                    </p>
                  </div>
                  <TimezoneSelect
                    value={chartAppearance.timezone || "UTC"}
                    onChange={setTimezone}
                  />
                </div>
              </div>
            )}

            {activeTab === "drawings" && (
              <DrawingsTabContent />
            )}

            {activeTab === "app" && (
              <div className="space-y-4">
                <ToggleRow
                  label="Skip CSV preview"
                  description="Auto-load CSVs with detected settings, skipping the import dialog."
                  checked={autoLoadCsv}
                  onCheckedChange={setAutoLoadCsv}
                />
                {hasUpdateAvailable && (
                  <div
                    className="rounded-lg border p-3"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--lyt-warning) 10%, var(--bg-elevated))",
                      borderColor: "color-mix(in srgb, var(--lyt-warning) 30%, var(--border-default))",
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)" }}>
                          Update available
                        </p>
                        <p
                          className="mt-1 leading-4"
                          style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}
                        >
                          A new version is ready. Reload to apply it.
                        </p>
                      </div>
                      <span
                        className="rounded-full px-2 py-1 shrink-0"
                        style={{
                          backgroundColor: "color-mix(in srgb, var(--lyt-warning) 18%, transparent)",
                          color: "var(--lyt-warning)",
                          fontSize: "var(--text-xs)",
                          fontWeight: 600,
                        }}
                      >
                        UPDATE READY
                      </span>
                    </div>
                    <Button
                      type="button"
                      onClick={reloadApp}
                      className="interactive interactive-button mt-4 h-9 rounded-md focus-visible:ring-0"
                      style={{
                        backgroundColor: "var(--lyt-warning)",
                        color: "#0C0B08",
                      }}
                    >
                      Update & Reload
                    </Button>
                  </div>
                )}
                <div
                  className="rounded-lg border p-3"
                  style={{
                    backgroundColor: "var(--bg-elevated)",
                    borderColor: "var(--border-default)",
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)" }}>
                        Home Screen App
                      </p>
                      <p
                        className="mt-1 leading-4"
                        style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}
                      >
                        {isInstalled
                          ? "LYTICK is already installed for full-screen launch."
                          : "Install the workspace as an app for faster startup and cleaner focus."}
                      </p>
                    </div>
                    <span
                      className="rounded-full px-2 py-1"
                      style={{
                        backgroundColor: isInstalled
                          ? "color-mix(in srgb, var(--bullish) 18%, transparent)"
                          : "var(--surface-tint-hover)",
                        color: isInstalled ? "var(--text-primary)" : "var(--text-secondary)",
                        fontSize: "var(--text-xs)",
                        fontWeight: 600,
                      }}
                    >
                      {isInstalled ? "INSTALLED" : canInstall ? "READY" : "WAITING"}
                    </span>
                  </div>
                  <Button
                    type="button"
                    onClick={() => void promptInstall()}
                    disabled={isInstalled || !canInstall}
                    className="interactive interactive-button mt-4 h-9 rounded-md focus-visible:ring-0"
                    variant="outline"
                    style={{
                      backgroundColor: "transparent",
                      borderColor: "var(--border-default)",
                      color: "var(--text-primary)",
                    }}
                  >
                    {installLabel}
                  </Button>
                </div>

                <StorageStatusCard />

                <div
                  className="space-y-2 rounded-lg border p-3"
                  style={{
                    backgroundColor: "var(--bg-elevated)",
                    borderColor: "var(--border-default)",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                    <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)", fontWeight: 600 }}>
                      LYTICK Trading Workspace
                    </p>
                  </div>
                  <p style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                    Professional charting workspace for Deriv traders.
                  </p>
                  <div
                    className="flex items-center justify-between"
                    style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}
                  >
                    <span>Version {APP_VERSION}</span>
                    <span>Built for desktop</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex h-12 shrink-0 items-center justify-end gap-2 border-t px-4"
          style={{
            backgroundColor: "var(--bg-elevated)",
            borderColor: "var(--border-default)",
          }}
        >
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="interactive interactive-button h-8 rounded-md px-4 focus-visible:ring-0"
            style={{
              backgroundColor: "transparent",
              borderColor: "var(--border-default)",
              color: "var(--text-primary)",
              fontSize: "var(--text-sm)",
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleOk}
            className="interactive interactive-button h-8 rounded-md px-4 focus-visible:ring-0"
            style={{
              backgroundColor: "var(--accent-primary)",
              color: "var(--text-primary)",
              fontSize: "var(--text-sm)",
            }}
          >
            Ok
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Storage status card ───────────────────────────────────────────────────────

function StorageStatusCard() {
  const [info, setInfo] = useState<{ usageMB: number; quotaMB: number; persisted: boolean } | null>(null);

  useEffect(() => {
    if (!navigator.storage?.estimate) return;
    Promise.all([
      navigator.storage.estimate(),
      navigator.storage.persisted?.() ?? Promise.resolve(false),
    ]).then(([est, persisted]) => {
      const mb = (b: number | undefined) => Math.round((b ?? 0) / 1024 / 1024);
      setInfo({ usageMB: mb(est.usage), quotaMB: mb(est.quota), persisted });
    }).catch(() => {});
  }, []);

  if (!info) return null;

  const pct = info.quotaMB > 0 ? Math.min(100, Math.round((info.usageMB / info.quotaMB) * 100)) : 0;
  const barColor = pct > 80 ? "var(--error)" : pct > 60 ? "var(--lyt-warning)" : "var(--accent-primary)";

  return (
    <div
      className="space-y-2 rounded-lg border p-3"
      style={{ backgroundColor: "var(--bg-elevated)", borderColor: "var(--border-default)" }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
          <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)", fontWeight: 600 }}>
            Local Storage
          </p>
        </div>
        <span
          className="rounded-full px-2 py-0.5"
          style={{
            backgroundColor: info.persisted
              ? "color-mix(in srgb, var(--bullish) 18%, transparent)"
              : "color-mix(in srgb, var(--lyt-warning) 18%, transparent)",
            color: info.persisted ? "var(--bullish)" : "var(--lyt-warning)",
            fontSize: "var(--text-xs)",
            fontWeight: 600,
          }}
        >
          {info.persisted ? "DURABLE" : "BEST EFFORT"}
        </span>
      </div>
      <div
        className="h-1.5 w-full rounded-full overflow-hidden"
        style={{ backgroundColor: "var(--surface-tint)" }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      <p style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}>
        {info.usageMB} MB used of {info.quotaMB} MB
        {!info.persisted && " — browser may evict data under disk pressure"}
      </p>
    </div>
  );
}

// ── Drawings tab ──────────────────────────────────────────────────────────────

const LINE_WIDTHS = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
] as const;

const LINE_STYLES = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
] as const;

function DrawingsTabContent() {
  const [selectedTool, setSelectedTool] = useState<DrawingToolId>("trendLine");
  const toolDefaults = useDrawingStore((s) => s.toolDefaults);
  const setToolDefault = useDrawingStore((s) => s.setToolDefault);
  const resetAllToolDefaults = useDrawingStore((s) => s.resetAllToolDefaults);

  const defaults = toolDefaults[selectedTool];

  return (
    <div className="space-y-3">
      {/* Tool picker */}
      <div
        className="rounded-lg border px-3 py-2.5"
        style={{ backgroundColor: "var(--bg-elevated)", borderColor: "var(--border-default)" }}
      >
        <p
          className="mb-2 text-xs font-medium uppercase tracking-wider"
          style={{ color: "var(--text-muted)" }}
        >
          Tool
        </p>
        <Select value={selectedTool} onValueChange={(v) => setSelectedTool(v as DrawingToolId)}>
          <SelectTrigger
            style={{
              backgroundColor: "var(--bg-surface)",
              borderColor: "var(--border-default)",
              color: "var(--text-primary)",
              fontSize: "var(--text-sm)",
              height: 32,
              width: "100%",
            }}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            style={{ backgroundColor: "var(--bg-overlay)", borderColor: "var(--border-default)" }}
          >
            {DRAWING_TOOLS.map((tool) => (
              <SelectItem
                key={tool.id}
                value={tool.id}
                style={{ fontSize: "var(--text-sm)", color: "var(--text-primary)" }}
              >
                {tool.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Defaults editor */}
      <div
        className="rounded-lg border divide-y"
        style={{
          backgroundColor: "var(--bg-elevated)",
          borderColor: "var(--border-default)",
          "--tw-divide-color": "var(--border-default)",
        } as React.CSSProperties}
      >
        {/* Color */}
        <div className="flex min-h-11 items-center justify-between gap-3 px-3 py-2">
          <div className="min-w-0">
            <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)" }}>Color</p>
            <p className="mt-1 leading-4" style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}>
              Default color for new {DRAWING_TOOLS.find((t) => t.id === selectedTool)?.label ?? selectedTool} drawings.
            </p>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="size-7 shrink-0 rounded-md border-2 transition-transform hover:scale-110"
                style={{
                  backgroundColor: defaults.color,
                  borderColor: "var(--border-default)",
                }}
                aria-label="Select color"
              />
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-3"
              style={{ backgroundColor: "var(--bg-overlay)", borderColor: "var(--border-default)" }}
            >
              <ColorPicker
                value={defaults.color}
                onChange={(color) => setToolDefault(selectedTool, { color })}
                opacity={defaults.opacity}
                onOpacityChange={(opacity) => setToolDefault(selectedTool, { opacity })}
                showOpacity
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Line width */}
        <div className="flex min-h-11 items-center justify-between gap-3 px-3 py-2">
          <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)" }}>Line width</p>
          <div
            className="flex shrink-0 overflow-hidden rounded-md border"
            style={{ borderColor: "var(--border-default)" }}
          >
            {LINE_WIDTHS.map((opt) => {
              const active = defaults.lineWidth === Number(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setToolDefault(selectedTool, { lineWidth: Number(opt.value) })}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: active ? "var(--accent-primary)" : "transparent",
                    color: active ? "var(--text-primary)" : "var(--text-secondary)",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Line style */}
        <div className="flex min-h-11 items-center justify-between gap-3 px-3 py-2">
          <p style={{ color: "var(--text-primary)", fontSize: "var(--text-base)" }}>Line style</p>
          <div
            className="flex shrink-0 overflow-hidden rounded-md border"
            style={{ borderColor: "var(--border-default)" }}
          >
            {LINE_STYLES.map((opt) => {
              const active = defaults.lineStyle === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setToolDefault(selectedTool, { lineStyle: opt.value })}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: active ? "var(--accent-primary)" : "transparent",
                    color: active ? "var(--text-primary)" : "var(--text-secondary)",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reset */}
      <Button
        type="button"
        variant="outline"
        onClick={resetAllToolDefaults}
        className="h-8 w-full rounded-md text-xs"
        style={{
          backgroundColor: "transparent",
          borderColor: "var(--border-default)",
          color: "var(--text-secondary)",
        }}
      >
        Reset all drawing defaults
      </Button>
    </div>
  );
}
